// Stage 2.6D — the purchase order's "Labels & Packaging" pack: batch numbers, what each label kind is counted by,
// and how many to print. Shared by the server (which builds and freezes the pack) and the screen (outputs).
import type { DesignKind, LabelData, LabelDesign } from "./labels.js";

/** Batch number format: {po} PO number, {yymm} / {yyyy} / {yy} / {mm} from the order date. Default "{po}-{yymm}" → PO054-2609. */
export const DEFAULT_BATCH_FORMAT = "{po}-{yymm}";
export const BATCH_TOKENS: Array<[string, string]> = [["{po}", "PO number"], ["{yymm}", "Year + month of the order date (2609)"], ["{yyyy}", "Year (2026)"], ["{yy}", "Year (26)"], ["{mm}", "Month (09)"]];
export function normalizeBatchFormat(v: unknown): string {
  const s = String(v ?? "").trim().slice(0, 60);
  return s && s.includes("{po}") ? s : DEFAULT_BATCH_FORMAT;
}
export function batchNumber(format: string, poNumber: string, orderDate: string | null): string {
  const d = /^(\d{4})-(\d{2})/.exec(orderDate || "");
  const yyyy = d?.[1] || "", mm = d?.[2] || "", yy = yyyy.slice(2);
  return normalizeBatchFormat(format).replace(/\{po\}/g, poNumber).replace(/\{yymm\}/g, yy + mm).replace(/\{yyyy\}/g, yyyy).replace(/\{yy\}/g, yy).replace(/\{mm\}/g, mm)
    .replace(/[-_/.\s]+$/, "").slice(0, 80);
}

/** What goes in a pack, in order. Per-piece labels are counted from the PO quantities; carton labels per carton;
 *  polybag and carton marks are print artwork (one sheet per design / carton group). */
export const PACK_KINDS: Array<{ kind: DesignKind; label: string; per: "piece" | "carton" | "artwork"; default: boolean }> = [
  { kind: "swing_tag", label: "Swing tags", per: "piece", default: true },
  { kind: "size_sticker", label: "Polybag stickers", per: "piece", default: true },
  { kind: "wash_care", label: "Wash care labels", per: "piece", default: true },
  { kind: "brand_label", label: "Brand labels", per: "piece", default: false },
  { kind: "size_tab", label: "Size tabs", per: "piece", default: false },
  { kind: "ppe_label", label: "FR / PPE labels", per: "piece", default: false },
  { kind: "polybag", label: "Polybag", per: "artwork", default: true },
  { kind: "carton", label: "Carton marks", per: "artwork", default: true },
  { kind: "carton_label", label: "Carton labels", per: "carton", default: true },
];
export const PACK_KIND_LABEL = Object.fromEntries(PACK_KINDS.map((k) => [k.kind, k.label])) as Record<DesignKind, string>;

export type QtyMode = "po" | "one";
export interface PackSettings { sparePct: number; mode: QtyMode; kinds: DesignKind[]; lineIds: string[] }
/** The organisation's default spare % for new packs (Settings ▸ Garment Codes). */
export const DEFAULT_LABEL_SPARE = 3;
export function normalizePackSettings(v: any, lineIds: string[], defaultSpare = 0): PackSettings {
  const known = new Set(PACK_KINDS.map((k) => k.kind));
  const kinds = Array.isArray(v?.kinds) ? v.kinds.filter((k: any) => known.has(k)) : PACK_KINDS.filter((k) => k.default).map((k) => k.kind);
  const lines = Array.isArray(v?.lineIds) ? v.lineIds.filter((x: any) => lineIds.includes(x)) : lineIds;
  const spare = v?.sparePct === undefined || v?.sparePct === null || v?.sparePct === "" ? defaultSpare : Number(v.sparePct) || 0;
  return { sparePct: Math.max(0, Math.min(100, Math.round(spare * 10) / 10)), mode: v?.mode === "one" ? "one" : "po", kinds, lineIds: lines };
}

/** How many of a per-piece label to print for one size: the PO quantity plus the spare %, rounded up; or one each. */
export const labelsFor = (qty: number, s: Pick<PackSettings, "sparePct" | "mode">) => (s.mode === "one" ? (qty > 0 ? 1 : 0) : Math.ceil(qty * (1 + s.sparePct / 100)));

/** One design on a line, as the pack holds it (the pinned revision, or this PO's own override). */
export interface PackDesign {
  kind: DesignKind; design: LabelDesign; artworkId: string; code: string; name: string; revision: number; revisionLabel: string; status: string;
  override: null | { note: string; by: string; at: string };
}
export interface PackCarton { list: number; from: number; to: number; of: number; sizes: Record<string, number>; qty: number; netKg: number; grossKg: number; estimated: boolean }
export interface PackLine {
  lineId: string; lineNo: number; code: string; description: string; colour: string; sizes: string[]; qtyBySize: Record<string, number>;
  techpack: { id: string; revision: number; label: string } | null;
  data: LabelData; skus: Record<string, { sku: string; ean: string }>;
  designs: Partial<Record<DesignKind, PackDesign>>; cartons: PackCarton[];
  /** Built from a Tech Pack that isn't approved yet: one-of-each approval sheet only, watermarked DRAFT. */
  draft?: boolean;
  /** The Tech Pack's trims, labels and packaging with their pictures (frozen with the pack once issued). */
  trims?: PackTrim[];
}
export interface PackTrim { type: string; code: string; description: string; colour: string; size: string; placement: string; qty: string; unit: string; imageFileId: string }
/** Printed across every label that isn't approved for production. */
export const DRAFT_WATERMARK = "DRAFT – NOT FOR PRODUCTION";
/** A line prints with the DRAFT watermark when its Tech Pack or one of its designs isn't approved. */
export const isDraftDesign = (l: PackLine, d: PackDesign | undefined) => !!l.draft || (!!d && !d.override && d.status !== "approved");
export interface PackContent { poNumber: string; poRevision: number; batch: string; supplier: string; lines: PackLine[]; builtAt: string }

/** The data one label prints: the line's style data with this size's SKU and EAN, the batch number and the PO. */
export function labelDataFor(c: PackContent, l: PackLine, size: string, carton?: PackCarton): LabelData {
  const s = l.skus[size] || { sku: "", ean: "" };
  const cartonNo = carton ? (carton.from === carton.to ? `${carton.from} of ${carton.of}` : `${carton.from}–${carton.to} of ${carton.of}`) : undefined;
  return {
    ...l.data, colour: l.colour, size, sku: s.sku, ean: s.ean, batch: c.batch, po: c.poNumber, stickerDesign: l.designs.size_sticker?.design,
    ...(carton ? { cartonNo, quantity: `${carton.qty} pcs`, sizeBreakdown: [...l.sizes, ...Object.keys(carton.sizes).filter((k) => !l.sizes.includes(k))].filter((k) => carton.sizes[k]).map((k) => `${k}×${carton.sizes[k]}`).join(" ") } : {}),
  };
}

/** Every label to print, with its count: per size for per-piece labels, per carton for carton labels, one per artwork. */
export interface PrintItem { lineId: string; kind: DesignKind; size: string; count: number; carton?: PackCarton; key: string }
export function printItems(c: PackContent, s: PackSettings): PrintItem[] {
  const out: PrintItem[] = [];
  // Lines whose Tech Pack isn't approved print only as a one-of-each approval sheet, never production quantities.
  for (const l of c.lines.filter((x) => s.lineIds.includes(x.lineId) && !(x.draft && s.mode === "po"))) {
    for (const k of PACK_KINDS.filter((x) => s.kinds.includes(x.kind) && l.designs[x.kind])) {
      if (k.per === "piece") {
        // A separate barcode sticker attached to each swing tag (swing tag option) doubles the polybag stickers.
        const perPiece = k.kind === "size_sticker" && s.kinds.includes("swing_tag") && l.designs.swing_tag?.design.pack?.extraSticker ? 2 : 1;
        for (const size of l.sizes) {
          const n = labelsFor(Number(l.qtyBySize[size]) || 0, s) * perPiece;
          if (n) out.push({ lineId: l.lineId, kind: k.kind, size, count: n, key: `${l.lineId}|${k.kind}|${size}` });
        }
      } else if (k.per === "carton") {
        // Carton labels are optional on the carton design ("Also print carton labels").
        if (l.designs.carton?.design.pack?.cartonLabels === false) continue;
        // A carton label per carton ("one of each" gives one per carton group, for approval).
        for (const ct of l.cartons) {
          if (s.mode === "one") out.push({ lineId: l.lineId, kind: k.kind, size: "", count: 1, carton: ct, key: `${l.lineId}|${k.kind}|${ct.list}:${ct.from}` });
          else for (let n = ct.from; n <= ct.to; n++) out.push({ lineId: l.lineId, kind: k.kind, size: "", count: 1, carton: { ...ct, from: n, to: n }, key: `${l.lineId}|${k.kind}|${ct.list}:${n}` });
        }
      } else if (k.kind === "carton") {
        for (const ct of l.cartons) out.push({ lineId: l.lineId, kind: k.kind, size: "", count: 1, carton: ct, key: `${l.lineId}|${k.kind}|${ct.list}:${ct.from}` });
      } else out.push({ lineId: l.lineId, kind: k.kind, size: l.sizes.find((z) => Number(l.qtyBySize[z]) > 0) || l.sizes[0] || "", count: 1, key: `${l.lineId}|${k.kind}` });
    }
  }
  return out;
}
